import { useEffect, useId, useRef, type PropsWithChildren, type ReactNode } from "react";

interface ModalProps extends PropsWithChildren {
  open: boolean;
  title: string;
  description?: string;
  footer?: ReactNode;
  onClose: () => void;
}

const FOCUSABLE = "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

export function Modal({ open, title, description, footer, onClose, children }: ModalProps) {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!open) return;
    returnFocusRef.current = document.activeElement as HTMLElement;
    const dialog = dialogRef.current;
    const initialFocus = dialog?.querySelector<HTMLElement>(FOCUSABLE);
    if (initialFocus) initialFocus.focus();
    else dialog?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key !== "Tab" || !dialog) return;
      const elements = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (elements.length === 0) { event.preventDefault(); return; }
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener("keydown", keydown);
    return () => { document.removeEventListener("keydown", keydown); returnFocusRef.current?.focus(); };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <>
      <div className="modal d-block" role="presentation"><div className="modal-dialog" role="document"><div ref={dialogRef} className="modal-content" role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={description ? descriptionId : undefined} tabIndex={-1}>
      <div className="modal-header"><h2 id={titleId} className="modal-title h5">{title}</h2><button type="button" className="close" aria-label="關閉" onClick={onClose}><span aria-hidden="true">×</span></button></div>
      <div className="modal-body">{description && <p id={descriptionId}>{description}</p>}{children}</div>
      {footer && <div className="modal-footer">{footer}</div>}
      </div></div></div>
      <div className="modal-backdrop show" aria-hidden="true" />
    </>
  );
}
